<template>
  <div>
    <div class="row">
      <div class="form-group col-lg-12">
        <label for="client">{{ $t('client') }}</label>
        <Combobox
          class="bg-white"
          name='client'
          :placeholder="$t('select_a_item')"
          label="CLI_DSC"
          :getOptionLabel="client => client.CLI_ID + ' - ' + client.CLI_DSC"
          :options="clients"
          v-model="clientSelected"
          :initial-value="clientSelected"
          :reduce="client => client.CLI_ID"
          v-validate="'required'"
          :hasErrors="errors.has('client')"
        />

        <small v-show="errors.has('client')" class="form-text">
          {{ errors.first('client') }}
        </small>
      </div>

      <div class="form-group col-lg-12" v-show="clientSelected">
        <label for="contract">{{ $t('contract') }}</label>
        <Combobox
          class="bg-white"
          name='contract'
          :placeholder="$t('select_a_item')"
          label="CLI_DSC"
          :getOptionLabel="contract => contract.id_contrato + ' - ' + contract.contracts"
          :options="contracts"
          v-model="contractsSelected"
          :initial-value="contractsSelected"
          :reduce="contract => contract.id_contrato"
          :disabled="!clientSelected"
          multiple
        />
      </div>

      <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('period_start') }">
        <label for="period_start">{{ $t('period_start') }}</label>
        <input
          type="date"
          id="period_start"
          name="period_start"
          class="form-control"
          :placeholder="$t('ph_period_start')"
          v-model="periodStart"
          v-validate="'required'"
        />

        <small v-show="errors.has('period_start')" class="form-text">
          {{ errors.first('period_start') }}
        </small>
      </div>

      <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('period_end') }">
        <label for="period_end">{{ $t('period_end') }}</label>
        <input
          type="date"
          id="period_end"
          name="period_end"
          class="form-control"
          :placeholder="$t('ph_period_end')"
          v-model="periodEnd"
          v-validate="'required'"
        />

        <small v-show="errors.has('period_end')" class="form-text">
          {{ errors.first('period_end') }}
        </small>
      </div>

      <div class="form-group col-lg-4">
        <label for="status">{{ $t('status') }}</label>
        <Combobox
          class="bg-white"
          name='status'
          :placeholder="$t('select_a_item')"
          label="label"
          :options="allStatus"
          v-model="statusSelected"
          :reduce="status => status.value"
        />
      </div>
    </div>

    <LoaderButton
      @click="getRoteirizations"
    >
      {{ $t('search') }}
    </LoaderButton>

    <div class="row">
      <div class="col-12">
        <div style="display: flex; flex: 1; justify-content: space-between;">
          <h3 v-show="roteirizations.length">Total de {{ roteirizations.length }} roteirizações</h3>

          <b-pagination
            v-show="roteirizations.length"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            aria-controls="roteirizations_data"
            align="right"
          />
        </div>

        <b-table
          id="roteirizations_data"
          responsive='xl'
          bordered
          hover
          class="bg-white"
          :items="roteirizations"
          :fields='fields'
          :per-page="perPage"
          :current-page="currentPage"
        >
          <template #cell(status)="{item}">
            <div
              :class="{
                'bg-primary-frotix': item.status === 'SUCESSO',
                'bg-danger': item.status === 'ERRO'
              }"
              style="
                border-radius: 50px;
                padding-top: 5px;
                padding-bottom: 5px;
                font-weight: bold;
              "
            >
              {{ item.status }}
            </div>
          </template>
        </b-table>

        <b-pagination
          v-show="roteirizations.length"
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          aria-controls="roteirizations_data"
          align="right"
        />
      </div>
    </div>
  </div>
</template>

<script>
import LoaderButton from '@/components/button/LoaderButton'
import Combobox from '@/components/shared/ComboBox'
import { request } from '@/lib/http'
import { apiBaseUrl } from '@/lib/config'
import axios from 'axios'
import { mapState } from 'vuex'

export default {
  name: 'Roteirization',

  components: {
    LoaderButton,
    Combobox
  },

  data () {
    return {
      currentPage: 1,
      perPage: 25,
      clientSelected: undefined,
      contractsSelected: [],
      periodStart: '',
      periodEnd: '',
      statusSelected: undefined,
      allStatus: [
        { label: 'SUCESSO', value: 'success' },
        { label: 'ERRO', value: 'error' }
      ],
      fields: [
        {
          key: 'createdAt',
          label: this.$t('createdAt')
        },
        {
          key: 'vehicles',
          label: this.$t('vehicles')
        },
        {
          key: 'invoice',
          label: this.$t('invoice')
        },
        {
          key: 'invoiceCreatedAt',
          label: this.$t('invoice_created_at')
        },
        {
          key: 'client',
          label: this.$t('client')
        },
        {
          key: 'sourceFile',
          label: 'Arquivo'
        },
        {
          key: 'status',
          label: this.$t('status'),
          class: 'text-center'
        },
      ],
      roteirizations: []
    }
  },

  watch: {
    async clientSelected(value) {
      if (!value) {
        this.contractsSelected = []

        return
      }

      await this.fetchContracts(value)
    }
  },

  computed: {
    ...mapState({
      clients: state => state.arena.clients,
      contracts: state => state.contracts.all
    }),

    rows() {
      return this.roteirizations.length
    }
  },

  methods: {
    async getRoteirizations() {
      const isValid = await this.$validator.validateAll()

      if (!isValid) {
        const err = new Error(this.$t('invalid_form_fields'))

        this.$store.dispatch('notify/error', err)

        return
      }

      try {
        this.loading(true)

        let params = {
          client: this.clientSelected,
          start: this.periodStart,
          end: this.periodEnd,
          status: this.statusSelected
        }

        if (this.contractsSelected.length) {
          params.contracts = this.contractsSelected
        }

        this.roteirizations = await request({
          url: 'roteirizations',
          method: 'get',
          params
        })
      } catch (error) {
        console.log('[ERROR-GETTING-ROTEIRIZATIONS] => ', error)
      } finally {
        this.loading(false)
      }
    },

    async generateCSV() {
      const isValid = await this.$validator.validateAll()

      if (!isValid) {
        const err = new Error(this.$t('invalid_form_fields'))

        this.$store.dispatch('notify/error', err)

        return
      }

      try {
        this.loading(true)

        const urlRequest = `${apiBaseUrl()}roteirizations/generate-csv`
        const defaultParams = `?client=${this.clientSelected}&start=${this.periodStart}&end=${this.periodEnd}`
        const params = this.contractsSelected ? `${defaultParams}&contracts=${this.contractsSelected}` : defaultParams
        const client = this.clients.find(client => client.CLI_ID == this.clientSelected)
        const clientName = `${client.CLI_ID} - ${client.CLI_DSC}`

        const data = await axios.get(urlRequest+params, { responseType: 'blob' })

        const url = window.URL.createObjectURL(new Blob([data.data], { type: 'text/csv; charset=latin1' }))
        const link = document.createElement('a')
        link.href  = url
        link.setAttribute('download', `${clientName || 'roteirizacoes'}.csv`)
        document.body.appendChild(link)
        link.click()
      } catch (error) {
        console.log('[ERROR-GENERATING-CSV] => ', error)
      } finally {
        this.loading(false)
      }
    },

    loading(show) {
      this.$store.dispatch('overlay/show', show)
    },

    async fetchContracts(clientId) {
      try {
        this.loading(true)

        await this.$store.dispatch('contracts/fetchByClient', clientId)
      } catch (error) {
        console.log('[ERROR-FETCH-CONTRACTS] => ', error)
      } finally {
        this.loading(false)
      }
    }
  },

  async mounted () {
    await this.$store.dispatch('arena/fetchClients')

    const global = this

    $('#btn_generate_csv_roteirizations').on('click', function() {
      global.generateCSV()
    })
  },
}
</script>
